import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Notifications from './Notifications';
import api, { householdAPI } from '../services/api';
import { 
  LayoutDashboard,
  BarChart3,
  Settings,
  LogOut,
  User,
  Shield,
  Menu,
  X,
  Moon,
  Sun,
  Home,
  Bell,
  LayoutList
} from 'lucide-react';
import '../styles/Navbar.css';

function Navbar({ collapsed, onToggle, onLogout, user, mobileOpen }) {
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const profileMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const { mode, toggleMode } = useTheme();

  const isManager = user?.household_role === 'manager';
  const hasHousehold = !!user?.household_id;

  // Load notification count - memoized to avoid recreating on every render
  const loadNotificationCount = useCallback(async () => {
    try {
      let totalCount = 0;
      
      // Always get notification count
      const notifResponse = await api.get('/notifications/unread-count');
      totalCount += notifResponse.data.count;
      
      // Also get join requests count if manager
      if (isManager && hasHousehold) {
        try {
          const requestsResponse = await householdAPI.getJoinRequestsCount();
          totalCount += requestsResponse.data.count;
        } catch (e) {
          // Ignore if no join requests endpoint
        }
      }
      
      setNotificationCount(totalCount);
    } catch (error) {
      console.error('Failed to load notification count:', error);
    }
  }, [isManager, hasHousehold]);

  // Load notification count on mount and poll every 5 seconds for faster updates
  useEffect(() => {
    loadNotificationCount();
    const interval = setInterval(loadNotificationCount, 5000); // Every 5 seconds for faster updates
    return () => clearInterval(interval);
  }, [loadNotificationCount]);

  // Get profile image from user preferences or localStorage
  const profileImage = user?.preferences?.profile_image || localStorage.getItem('profile_image');
  
  // Get user initials for fallback
  const getInitials = () => {
    const first = user?.first_name?.[0] || '';
    const last = user?.last_name?.[0] || '';
    return (first + last).toUpperCase();
  };

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showProfileMenu || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu, showNotifications]);

  const navItems = [
    // Overview
    { path: '/overview', label: 'Overview', icon: LayoutDashboard },
    
    // Core bill management
    { path: '/manage', label: 'Manage', icon: LayoutList },
    
    // Analytics
    { path: '/stats', label: 'Stats', icon: BarChart3 },
    
    // Account/Settings
    { path: '/household', label: 'Household', icon: Home },
  ];

  return (
    <nav className={`navbar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="nav-header">
        <div className="nav-header-left">
          <button className="nav-toggle" onClick={onToggle} aria-label="Toggle navigation">
            {collapsed && !mobileOpen ? <Menu size={20} /> : <X size={20} />}
          </button>
          {(!collapsed || mobileOpen) && <h1 className="nav-title">Remidy</h1>}
        </div>
      </div>

      <ul className="nav-menu">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.path} className="nav-item">
              <Link
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                title={item.label}
              >
                <Icon size={20} className="nav-icon" />
                {(!collapsed || mobileOpen) && <span className="nav-label">{item.label}</span>}
              </Link>
            </li>
          );
        })}
      </ul>

      {isAdmin && (
        <div className="admin-section">
          <Link
            to="/admin"
            className={`admin-link ${location.pathname === '/admin' ? 'active' : ''}`}
            title="Admin"
          >
            <Shield size={20} className="admin-icon" />
            {(!collapsed || mobileOpen) && <span className="admin-label">Admin</span>}
          </Link>
        </div>
      )}

      <div className="nav-footer">
        <div className="footer-actions">
          <div className="profile-section" ref={profileMenuRef}>
            <button 
              className="profile-button" 
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              aria-label="Profile menu"
            >
              {profileImage ? (
                <img 
                  src={profileImage} 
                  alt="Profile" 
                  className="profile-image-navbar" 
                />
              ) : (
                <div className="profile-avatar-navbar">
                  {getInitials()}
                </div>
              )}
              {notificationCount > 0 && <span className="profile-notification-badge">{notificationCount}</span>}
            </button>
            
            {showProfileMenu && (
              <div className="profile-menu">
                <Link 
                  to="/settings" 
                  className="profile-menu-header-link"
                  onClick={() => setShowProfileMenu(false)}
                >
                  <div className="profile-menu-header">
                    <div className="profile-avatar">
                      {profileImage ? (
                        <img 
                          src={profileImage} 
                          alt="Profile" 
                          className="profile-image-menu" 
                        />
                      ) : (
                        <User size={24} />
                      )}
                    </div>
                    <div className="profile-info">
                      <div className="profile-name">{user?.first_name} {user?.last_name}</div>
                      <div className="profile-email">{user?.email || 'user@example.com'}</div>
                      {isAdmin && <div className="profile-role">Administrator</div>}
                    </div>
                  </div>
                </Link>
                
                <div className="profile-menu-divider"></div>
                
                {/* Notifications button - opens separate popup */}
                <button 
                  className="profile-menu-item"
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowNotifications(true);
                  }}
                >
                  <Bell size={18} className="profile-menu-icon" />
                  <span>Notifications</span>
                  {notificationCount > 0 && (
                    <span className="profile-menu-badge">{notificationCount}</span>
                  )}
                </button>
                
                <div className="profile-menu-divider"></div>
                
                <Link 
                  to="/settings" 
                  className="profile-menu-item"
                  onClick={() => setShowProfileMenu(false)}
                >
                  <Settings size={18} className="profile-menu-icon" />
                  <span>Settings</span>
                </Link>
                
                <button 
                  className="profile-menu-item"
                  onClick={() => {
                    toggleMode();
                    setShowProfileMenu(false);
                  }}
                >
                  {mode === 'light' ? (
                    <Moon size={18} className="profile-menu-icon" />
                  ) : (
                    <Sun size={18} className="profile-menu-icon" />
                  )}
                  <span>{mode === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                </button>
                
                <div className="profile-menu-divider"></div>
                
                <button 
                  className="profile-menu-item logout"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                >
                  <LogOut size={18} className="profile-menu-icon" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Notifications Popup */}
      {showNotifications && (
        <div className="notifications-popup-container" ref={notificationsRef}>
          <Notifications user={user} onCountChange={setNotificationCount} onClose={() => setShowNotifications(false)} />
        </div>
      )}
    </nav>
  );
}

export default Navbar;
